import React from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { COLORS } from '../config/colors';
import { strings } from '../strings';
import ListIcon from './icons/ListIcon';

type StationListToggleButtonProps = {
  isOpen: boolean;
  onPress: () => void;
};

/**
 * Toggles the station list. It is the top button of the map's right-hand
 * column (above `NewsButton` and `GuidesButton`) and stays visible while the
 * list is open so it can also close it again; screen readers announce its
 * expanded state through `accessibilityState`.
 */
const StationListToggleButton = ({
  isOpen,
  onPress,
}: StationListToggleButtonProps) => {
  const insets = useSafeAreaInsets();

  return (
    <Pressable
      accessibilityHint={strings.stationsListToggleHint}
      accessibilityLabel={
        isOpen ? strings.hideStationsList : strings.showStationsList
      }
      accessibilityRole="button"
      accessibilityState={{ expanded: isOpen }}
      onPress={onPress}
      style={({ pressed }) => [
        styles.button,
        // Same look as the other floating buttons; it sits at the top of the
        // column, 12 below the top inset.
        { top: insets.top + 12, right: 12 },
        pressed && styles.buttonPressed,
      ]}
    >
      <ListIcon color={COLORS.ink} />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  button: {
    position: 'absolute',
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 15,
    backgroundColor: COLORS.surface,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  buttonPressed: {
    opacity: 0.8,
  },
});

export default StationListToggleButton;
